import React, { FC, useEffect, useRef, useState } from 'react';
import { Modal } from 'react-bootstrap-v5';
import { shallowEqual, useSelector } from 'react-redux';
import { PageLink, PageTitle } from '../../../../rbts/layout/core';
import { KTSVG, SimpleAlert } from '../../../../rbts/helpers';
import { RootState } from '../../../../setup';
import {
  getRostrosService,
  createRostroService,
  updateRostroService,
  deleteRostroService,
  recargarSaldoService,
  RostroCliente,
} from '../../../service/face_recognition/FaceRecognitionService';
import { backendFileUrl } from '../backendUrl';
import { obtenerTicket, conTicket } from '../streamTicket';
import { buscarWalletUsersService, WalletUserBusqueda } from '../../../service/face_recognition/FaceRecognitionService';

const API_URL = process.env.REACT_APP_API;
const SNAPSHOT_URL = `${API_URL}faceRecognition/snapshot`;
const breadCrumbs: Array<PageLink> = [];

const CatalogoRostrosPage: FC = () => {
  const token = useSelector<RootState>(({ auth }) => (auth as any).accessToken, shallowEqual) as string;

  const [rostros, setRostros] = useState<RostroCliente[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showRecargaModal, setShowRecargaModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [editingRostro, setEditingRostro] = useState<RostroCliente | null>(null);
  const [selectedRostro, setSelectedRostro] = useState<RostroCliente | null>(null);
  const [saving, setSaving] = useState(false);

  // Formulario crear/editar
  const [nombre, setNombre] = useState('');
  const [telefono, setTelefono] = useState('');
  const [enrolError, setEnrolError] = useState<string | null>(null);

  // ─── Monedero de la Wallet vinculado ───
  // Se guarda el ID (llave real). El correo solo se muestra: no es único por sí
  // solo, la unicidad en wallet_users es de la pareja (email, evento_id).
  const [walletUserId, setWalletUserId] = useState<number | null>(null);
  const [walletTexto, setWalletTexto] = useState('');          // lo que escribe el operador
  const [walletOpciones, setWalletOpciones] = useState<WalletUserBusqueda[]>([]);
  const [walletBuscando, setWalletBuscando] = useState(false);
  const [walletSel, setWalletSel] = useState<WalletUserBusqueda | null>(null);
  const walletDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const ticketRef = useRef('');
  const [fotoFile, setFotoFile] = useState<File | null>(null);
  const [fotoPreview, setFotoPreview] = useState<string | null>(null);

  // Cámara en vivo
  const [liveSnapshotUrl, setLiveSnapshotUrl] = useState('');
  const [capturando, setCapturando] = useState(false);
  const cameraTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Recarga
  const [montoRecarga, setMontoRecarga] = useState('');

  useEffect(() => { loadRostros(); }, []);

  async function loadRostros() {
    setLoading(true);
    try {
      const data = await getRostrosService(token);
      setRostros(data);
    } catch {
      SimpleAlert('Error al cargar catálogo de rostros.', 3000, 'error');
    } finally {
      setLoading(false);
    }
  }

  /**
   * Busca monederos por nombre o correo. Se aplica debounce de 350 ms para no
   * disparar una petición por cada tecla; el backend exige mínimo 3 caracteres.
   */
  function onWalletTextoChange(texto: string) {
    setWalletTexto(texto);
    setWalletSel(null);
    setWalletUserId(null);
    setEnrolError(null);

    if (walletDebounceRef.current) clearTimeout(walletDebounceRef.current);
    if (texto.trim().length < 3) {
      setWalletOpciones([]);
      return;
    }
    walletDebounceRef.current = setTimeout(async () => {
      setWalletBuscando(true);
      try {
        setWalletOpciones(await buscarWalletUsersService(texto.trim(), token));
      } catch (_) {
        setWalletOpciones([]);
      } finally {
        setWalletBuscando(false);
      }
    }, 350);
  }

  function seleccionarWallet(u: WalletUserBusqueda) {
    setWalletSel(u);
    setWalletUserId(u.id);
    setWalletTexto(u.email);
    setWalletOpciones([]);
  }

  function limpiarWallet() {
    setWalletSel(null);
    setWalletUserId(null);
    setWalletTexto('');
    setWalletOpciones([]);
  }

  /*
   * El <img> de la cámara no puede enviar la cabecera Authorization, así que la
   * URL lleva un ticket de corta vida. Se pide antes de arrancar el refresco y
   * se revalida en cada tick: obtenerTicket() solo llama al backend cuando está
   * por expirar, el resto de las veces devuelve el de la caché.
   */
  async function startCamera() {
    try {
      ticketRef.current = await obtenerTicket(token);
    } catch (err) {
      SimpleAlert('No se pudo autorizar el acceso a la cámara.', 3000, 'error');
      return;
    }
    setLiveSnapshotUrl(conTicket(`${SNAPSHOT_URL}?t=${Date.now()}`, ticketRef.current));
    cameraTimerRef.current = setInterval(async () => {
      try {
        ticketRef.current = await obtenerTicket(token);
      } catch (_) { /* se conserva el ticket anterior */ }
      setLiveSnapshotUrl(conTicket(`${SNAPSHOT_URL}?t=${Date.now()}`, ticketRef.current));
    }, 2000);
  }

  function stopCamera() {
    if (cameraTimerRef.current) { clearInterval(cameraTimerRef.current); cameraTimerRef.current = null; }
    setLiveSnapshotUrl('');
  }

  async function capturarFoto() {
    setCapturando(true);
    try {
      const ticket = await obtenerTicket(token);
      const res = await fetch(conTicket(`${SNAPSHOT_URL}?t=${Date.now()}`, ticket));
      if (!res.ok) throw new Error('Sin respuesta del dispositivo');
      const blob = await res.blob();
      const file = new File([blob], 'captura.jpg', { type: 'image/jpeg' });
      setFotoFile(file);
      setFotoPreview(URL.createObjectURL(blob));
      stopCamera();
    } catch {
      SimpleAlert('No se pudo capturar la foto. Verifique la conexión con el dispositivo.', 3000, 'error');
    } finally {
      setCapturando(false);
    }
  }

  function recapturar() {
    setFotoFile(null);
    setFotoPreview(null);
    setEnrolError(null);
    startCamera();
  }

  function handleFotoChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    setEnrolError(null);
    stopCamera();
    setFotoFile(file);
    setFotoPreview(URL.createObjectURL(file));
  }

  function openCreate() {
    setEditingRostro(null);
    setNombre(''); setTelefono(''); setFotoFile(null); setFotoPreview(null);
    limpiarWallet();
    setShowModal(true);
    startCamera();
  }

  function openEdit(r: RostroCliente) {
    setEditingRostro(r);
    setNombre(r.nombre); setTelefono(r.telefono || ''); setFotoFile(null);
    // Precargar el monedero ya vinculado para que el operador lo vea y pueda cambiarlo
    if (r.wallet_user_id && r.wallet_email) {
      setWalletUserId(r.wallet_user_id);
      setWalletTexto(r.wallet_email);
      setWalletSel({
        id: r.wallet_user_id,
        nombre: r.wallet_nombre || r.nombre,
        email: r.wallet_email,
        folio: r.wallet_folio ?? null,
        saldo: Number(r.saldo) || 0,
      });
    } else {
      limpiarWallet();
    }
    setFotoPreview(r.foto_url ? backendFileUrl(r.foto_url) : null);
    setShowModal(true);
    if (!r.foto_url) startCamera();
  }

  function closeModal() {
    stopCamera();
    setEnrolError(null);
    if (walletDebounceRef.current) clearTimeout(walletDebounceRef.current);
    setShowModal(false);
  }

  async function handleSave() {
    if (!nombre.trim()) { SimpleAlert('El nombre es requerido.', 2000, 'error'); return; }
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append('nombre', nombre.trim());
      fd.append('telefono', telefono.trim());
      // El saldo ya no se captura: vive en el monedero de la Wallet.
      fd.append('wallet_user_id', walletUserId ? String(walletUserId) : '');
      if (fotoFile) fd.append('foto', fotoFile);

      const guardado = editingRostro
        ? await updateRostroService(editingRostro.id, fd, token)
        : await createRostroService(fd, token);

      /*
       * El registro se guarda en BD aunque el terminal rechace la foto. Sin este
       * aviso el operador daría de alta a alguien que jamás será reconocido y se
       * enteraría hasta que la persona esté frente a la caja.
       */
      if (guardado?.hikvision_error) {
        setEnrolError(guardado.hikvision_error);
        loadRostros();
        return;   // el modal permanece abierto para reintentar con otra foto
      }

      SimpleAlert(
        editingRostro ? 'Rostro actualizado y enrollado en el dispositivo.' : 'Rostro creado y enrollado en el dispositivo.',
        2000,
        'success'
      );
      closeModal();
      loadRostros();
    } catch (err: any) {
      SimpleAlert(err?.response?.data?.message || 'Error al guardar.', 3000, 'error');
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete() {
    if (!selectedRostro) return;
    setSaving(true);
    try {
      await deleteRostroService(selectedRostro.id, token);
      SimpleAlert('Rostro desactivado correctamente.', 2000, 'success');
      setShowDeleteModal(false);
      loadRostros();
    } catch {
      SimpleAlert('Error al desactivar.', 2000, 'error');
    } finally {
      setSaving(false);
    }
  }

  async function handleRecarga() {
    if (!selectedRostro) return;
    const monto = parseFloat(montoRecarga);
    if (isNaN(monto) || monto <= 0) { SimpleAlert('Ingresa un monto válido.', 2000, 'error'); return; }
    setSaving(true);
    try {
      const result = await recargarSaldoService(selectedRostro.id, monto, token);
      SimpleAlert(`Saldo actualizado: $${result.saldo.toFixed(2)}`, 2000, 'success');
      setShowRecargaModal(false);
      setMontoRecarga('');
      loadRostros();
    } catch (err: any) {
      SimpleAlert(err?.response?.data?.message || 'Error al recargar.', 3000, 'error');
    } finally {
      setSaving(false);
    }
  }

  return (
    <>
      <PageTitle breadcrumbs={breadCrumbs}>Catálogo de Rostros</PageTitle>

      <div className="card">
        <div className="card-header border-0 pt-5">
          <h3 className="card-title fw-bold fs-4">Clientes Face Recognition</h3>
          <div className="card-toolbar">
            <button className="btn btn-sm btn-primary" onClick={openCreate} disabled={loading}>
              <KTSVG path="/media/icons/duotune/general/gen035.svg" className="svg-icon-3 me-2" />
              Nuevo Rostro
            </button>
          </div>
        </div>

        <div className="card-body py-4">
          {loading ? (
            <div className="d-flex justify-content-center py-6">
              <span className="spinner-border text-primary" />
            </div>
          ) : rostros.length === 0 ? (
            <div className="alert alert-info">No hay rostros registrados aún.</div>
          ) : (
            <div className="table-responsive">
              <table className="table table-row-bordered align-middle gs-0 gy-3">
                <thead>
                  <tr className="fw-bold text-muted">
                    <th>Foto</th>
                    <th>Nombre</th>
                    <th>Teléfono</th>
                    <th>Saldo</th>
                    <th>Dispositivo</th>
                    <th>Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {rostros.map((r) => (
                    <tr key={r.id}>
                      <td>
                        {r.foto_url ? (
                          <img
                            src={backendFileUrl(r.foto_url)}
                            alt={r.nombre}
                            style={{ width: 48, height: 48, objectFit: 'cover', borderRadius: 8 }}
                          />
                        ) : (
                          <div
                            className="bg-light d-flex align-items-center justify-content-center rounded"
                            style={{ width: 48, height: 48 }}
                          >
                            <KTSVG path="/media/icons/duotune/communication/com006.svg" className="svg-icon-2 text-muted" />
                          </div>
                        )}
                      </td>
                      <td className="fw-bold">{r.nombre}</td>
                      <td className="text-muted">{r.telefono || '—'}</td>
                      <td>
                        {r.tiene_wallet ? (
                          <>
                            <span className={`badge fs-7 ${Number(r.saldo) > 0 ? 'badge-light-success' : 'badge-light-warning'}`}>
                              ${parseFloat(String(r.saldo)).toFixed(2)}
                            </span>
                            <div className="text-muted fs-8 mt-1">{r.wallet_email_mascarado}</div>
                          </>
                        ) : (
                          <span className="badge badge-light-danger fs-8" title="Sin monedero: no podrá comprar">
                            Sin Wallet
                          </span>
                        )}
                      </td>
                      <td>
                        {r.hikvision_face_id ? (
                          <span className="badge badge-light-primary">Enrollado</span>
                        ) : r.foto_url ? (
                          /* Tiene foto pero el terminal la rechazó: no es lo mismo que
                             no tener foto, y antes ambos casos se veían igual. */
                          <span className="badge badge-light-danger" title="La foto no pudo enrolarse en el terminal">
                            No enrollado
                          </span>
                        ) : (
                          <span className="badge badge-light-warning">Sin foto</span>
                        )}
                      </td>
                      <td>
                        <button className="btn btn-sm btn-icon btn-primary me-1" title="Editar" onClick={() => openEdit(r)}>
                          <KTSVG path="/media/icons/duotune/art/art005.svg" className="svg-icon-3" />
                        </button>
                        <button
                          className="btn btn-sm btn-icon btn-light-danger"
                          title="Desactivar"
                          onClick={() => { setSelectedRostro(r); setShowDeleteModal(true); }}
                        >
                          <KTSVG path="/media/icons/duotune/general/gen027.svg" className="svg-icon-3" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Modal Crear / Editar */}
      <Modal
        tabIndex={-1}
        aria-hidden="true"
        dialogClassName="modal-dialog modal-dialog-centered mw-550px"
        show={showModal}
        onHide={() => !saving && closeModal()}
      >
        <div className="modal-content">
          <div className="modal-header">
            <h2>{editingRostro ? 'Editar Rostro' : 'Nuevo Rostro'}</h2>
            <div className="btn btn-sm btn-icon btn-active-color-primary" onClick={() => !saving && closeModal()}>
              <KTSVG className="svg-icon-2" path="/media/icons/duotune/arrows/arr061.svg" />
            </div>
          </div>
          <div className="modal-body px-8 pb-8">
            {/* Foto del rostro */}
            <div className="d-flex flex-column align-items-center mb-6">
              {/* Vista previa */}
              <div
                className="rounded overflow-hidden bg-dark d-flex align-items-center justify-content-center mb-3"
                style={{ width: 220, height: 180, border: '2px solid #333' }}
              >
                {fotoPreview ? (
                  <img src={fotoPreview} alt="captura" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                ) : liveSnapshotUrl ? (
                  <img
                    src={liveSnapshotUrl}
                    alt="cámara"
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    onError={() => stopCamera()}
                  />
                ) : (
                  <div className="text-center text-muted">
                    <KTSVG path="/media/icons/duotune/communication/com006.svg" className="svg-icon-3x opacity-25" />
                    <div className="mt-1 fs-8">Sin señal de cámara</div>
                  </div>
                )}
              </div>

              {fotoPreview ? (
                /* Foto capturada — opciones para cambiarla */
                <div className="d-flex gap-2">
                  <button type="button" className="btn btn-sm btn-warning" onClick={recapturar}>
                    <KTSVG path="/media/icons/duotune/general/gen004.svg" className="svg-icon-4 me-1" />
                    Re-capturar
                  </button>
                  <label className="btn btn-sm btn-secondary mb-0" style={{ cursor: 'pointer' }}>
                    Cambiar foto
                    <input type="file" accept="image/*" style={{ display: 'none' }} onChange={handleFotoChange} />
                  </label>
                </div>
              ) : (
                /* Sin foto — subir archivo es la opción principal; captura del dispositivo como secundaria */
                <div className="d-flex flex-column align-items-center gap-2 w-100">
                  <label className="btn btn-primary w-100 mb-0" style={{ cursor: 'pointer' }}>
                    <KTSVG path="/media/icons/duotune/files/fil022.svg" className="svg-icon-4 me-1" />
                    Seleccionar foto desde equipo
                    <input type="file" accept="image/*" style={{ display: 'none' }} onChange={handleFotoChange} />
                  </label>
                  <div className="text-muted fs-8">— o —</div>
                  <button
                    type="button"
                    className="btn btn-sm btn-outline btn-outline-secondary text-white w-100"
                    onClick={capturarFoto}
                    disabled={capturando}
                  >
                    {capturando
                      ? <><span className="spinner-border spinner-border-sm me-1" />Capturando...</>
                      : <>Capturar desde dispositivo HIKVISION</>
                    }
                  </button>
                </div>
              )}
              <small className="text-muted mt-2 fs-8">Foto frontal clara del rostro (JPEG/PNG)</small>
            </div>

            <div className="mb-4">
              <label className="fw-bold mb-1">Nombre *</label>
              <input className="form-control" value={nombre} onChange={(e) => setNombre(e.target.value)} placeholder="Nombre completo" />
            </div>
            <div className="mb-4">
              <label className="fw-bold mb-1">Teléfono</label>
              <input className="form-control" value={telefono} onChange={(e) => setTelefono(e.target.value)} placeholder="10 dígitos" />
            </div>
            {enrolError && (
              <div className="alert alert-warning d-flex flex-column mb-5">
                <span className="fw-bold">Guardado en el sistema, pero NO enrollado en el terminal</span>
                <span className="fs-8 mt-1">
                  Esta persona no será reconocida en el cobro hasta enrolarla correctamente.
                  Prueba con otra foto: de frente, a la altura de los ojos, cabeza completa
                  con margen y buena luz frontal.
                </span>
                <span className="fs-8 text-muted mt-2">Detalle: {enrolError}</span>
              </div>
            )}

            {/* Monedero de la Wallet — de aquí sale el saldo con el que compra este rostro */}
            <div className="mb-4 position-relative">
              <label className="fw-bold mb-1">Correo de Wallet</label>
              <div className="input-group">
                <input
                  className="form-control"
                  value={walletTexto}
                  onChange={(e) => onWalletTextoChange(e.target.value)}
                  placeholder="Escribe nombre o correo (mín. 3 letras)"
                  autoComplete="off"
                />
                {walletSel && (
                  <button className="btn btn-light" type="button" onClick={limpiarWallet} title="Quitar vínculo">
                    ✕
                  </button>
                )}
              </div>

              {walletBuscando && <div className="text-muted fs-8 mt-1">Buscando…</div>}

              {/* Lista de coincidencias: nombre + correo + saldo, porque dos personas
                  pueden llamarse parecido y el correo solo no basta para distinguir */}
              {walletOpciones.length > 0 && (
                <div
                  className="position-absolute w-100 bg-body border rounded shadow-sm mt-1"
                  style={{ zIndex: 20, maxHeight: 240, overflowY: 'auto' }}
                >
                  {walletOpciones.map((u) => (
                    <button
                      key={u.id}
                      type="button"
                      className="btn btn-link text-start w-100 px-3 py-2 text-decoration-none border-0"
                      onClick={() => seleccionarWallet(u)}
                    >
                      <div className="fw-bold fs-7">{u.nombre}</div>
                      <div className="text-muted fs-8">{u.email}</div>
                      <div className="fs-8">
                        Saldo:{' '}
                        <span className={Number(u.saldo) > 0 ? 'text-success fw-bold' : 'text-warning fw-bold'}>
                          ${Number(u.saldo).toFixed(2)}
                        </span>
                        {u.folio ? <span className="text-muted"> · folio {u.folio}</span> : null}
                      </div>
                    </button>
                  ))}
                </div>
              )}

              {walletSel && (
                <div className="mt-2 p-3 rounded bg-light-primary">
                  <div className="fw-bold fs-7">{walletSel.nombre}</div>
                  <div className="text-muted fs-8">{walletSel.email}</div>
                  <div className="fs-8 mt-1">
                    Saldo del monedero:{' '}
                    <span className={Number(walletSel.saldo) > 0 ? 'text-success fw-bold fs-6' : 'text-warning fw-bold fs-6'}>
                      ${Number(walletSel.saldo).toFixed(2)}
                    </span>
                  </div>
                  {Number(walletSel.saldo) <= 0 && (
                    // Se advierte pero NO se bloquea: hay que poder pre-enrolar antes
                    // de que el cliente recargue desde la app.
                    <div className="text-warning fs-8 mt-2">
                      Este monedero está en ceros. Puedes enrolar de todas formas; el cliente
                      podrá recargar desde la Wallet antes de comprar.
                    </div>
                  )}
                </div>
              )}

              {!walletSel && walletTexto.trim().length >= 3 && !walletBuscando && walletOpciones.length === 0 && (
                <div className="text-muted fs-8 mt-1">Sin coincidencias en este evento.</div>
              )}

              <small className="text-muted fs-8">
                Varios rostros pueden compartir el mismo monedero. Sin vínculo, el rostro
                se enrola pero no podrá comprar.
              </small>
            </div>

            <div className="d-flex justify-content-end pt-2">
              <button className="btn btn-light me-3" onClick={closeModal} disabled={saving}>Cancelar</button>
              <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
                {saving ? <span className="spinner-border spinner-border-sm me-2" /> : null}
                {editingRostro ? 'Guardar cambios' : 'Crear y enrollar'}
              </button>
            </div>
          </div>
        </div>
      </Modal>

      {/* Modal Recarga */}
      <Modal
        tabIndex={-1}
        dialogClassName="modal-dialog modal-dialog-centered mw-400px"
        show={showRecargaModal}
        onHide={() => !saving && setShowRecargaModal(false)}
      >
        <div className="modal-content">
          <div className="modal-header">
            <h2>Recargar Saldo</h2>
            <div className="btn btn-sm btn-icon btn-active-color-primary" onClick={() => !saving && setShowRecargaModal(false)}>
              <KTSVG className="svg-icon-2" path="/media/icons/duotune/arrows/arr061.svg" />
            </div>
          </div>
          <div className="modal-body px-8 pb-8">
            <p className="text-muted mb-4">
              Cliente: <strong>{selectedRostro?.nombre}</strong><br />
              Saldo actual: <strong>${parseFloat(String(selectedRostro?.saldo || 0)).toFixed(2)}</strong>
            </p>
            <div className="mb-4">
              <label className="fw-bold mb-1">Monto a recargar ($)</label>
              <input
                className="form-control"
                type="number"
                min="1"
                value={montoRecarga}
                onChange={(e) => setMontoRecarga(e.target.value)}
                placeholder="0.00"
              />
            </div>
            <div className="d-flex justify-content-end">
              <button className="btn btn-light me-3" onClick={() => setShowRecargaModal(false)} disabled={saving}>Cancelar</button>
              <button className="btn btn-success" onClick={handleRecarga} disabled={saving}>
                {saving ? <span className="spinner-border spinner-border-sm me-2" /> : null}
                Recargar
              </button>
            </div>
          </div>
        </div>
      </Modal>

      {/* Modal Confirmar Desactivar */}
      <Modal
        tabIndex={-1}
        dialogClassName="modal-dialog modal-dialog-centered mw-400px"
        show={showDeleteModal}
        onHide={() => !saving && setShowDeleteModal(false)}
      >
        <div className="modal-content">
          <div className="modal-header">
            <h2>Desactivar Rostro</h2>
            <div className="btn btn-sm btn-icon btn-active-color-primary" onClick={() => setShowDeleteModal(false)}>
              <KTSVG className="svg-icon-2" path="/media/icons/duotune/arrows/arr061.svg" />
            </div>
          </div>
          <div className="modal-body px-8 pb-8">
            <p>
              ¿Desactivar a <strong>{selectedRostro?.nombre}</strong>? El rostro se eliminará del dispositivo HIKVISION.
            </p>
            <div className="d-flex justify-content-end pt-2">
              <button className="btn btn-light me-3" onClick={() => setShowDeleteModal(false)}>Cancelar</button>
              <button className="btn btn-danger" onClick={handleDelete} disabled={saving}>
                {saving ? <span className="spinner-border spinner-border-sm me-2" /> : null}
                Sí, desactivar
              </button>
            </div>
          </div>
        </div>
      </Modal>
    </>
  );
};

export { CatalogoRostrosPage };
